"use client";

import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useLayoutEffect, useRef } from "react";
import Spotlight, { SpotlightCardContainer } from "components/Cards/SpotlightCard/Spotlight";
import SpotlightCard from "components/Cards/SpotlightCard";
import { AssetFragment } from "contentful/gql/graphql";

export default function HomepageEnterpriseScale({
  json,
  assets,
}: {
  json: { cards: { title: string; body: string; icon: string }[] };
  assets: (AssetFragment | undefined)[] | undefined;
}) {
  const sectionRef = useRef<HTMLDivElement>(null);
  const tl = useRef<gsap.core.Timeline | null>(null);

  useLayoutEffect(() => {
    if (!sectionRef.current) return;

    let ctx = gsap.context(() => {
      gsap.registerPlugin(ScrollTrigger);

      // Check if the elements exist before creating animations
      const iconTrails = sectionRef.current?.querySelectorAll(".icon-trail");
      const icons = sectionRef.current?.querySelectorAll(".icon");
      const iconLabels = sectionRef.current?.querySelectorAll(".icon-label");

      if (!iconTrails?.length && !icons?.length && !iconLabels?.length) {
        return; // Exit early if no target elements found
      }

      tl.current = gsap.timeline({
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top 75%",
        },
      });

      // Only animate elements that exist
      if (iconTrails?.length) {
        tl.current.to(iconTrails, {
          y: 0,
          duration: 0.6,
          ease: "power2.out",
        });
      }

      if (icons?.length) {
        tl.current.to(
          icons,
          {
            y: 0,
            scale: 1,
            opacity: 1,
            duration: 0.3,
            ease: "power2.out",
          },
          iconTrails?.length ? "< +=0.4" : 0,
        );
      }

      if (iconLabels?.length) {
        tl.current.to(
          iconLabels,
          {
            opacity: 1,
            scale: 1,
            duration: 0.4,
          },
          icons?.length ? "< +=0.2" : 0,
        );
      }

      ScrollTrigger.create({
        trigger: sectionRef.current,
        onLeaveBack: () => {
          if (tl.current) {
            tl.current.pause(0);
          }
        },
      });
    }, sectionRef);

    return () => {
      ctx.revert();
      if (tl.current) {
        tl.current.kill();
        tl.current = null;
      }
    };
  }, []);

  return (
    <div ref={sectionRef} className="relative overflow-clip text-center">
      <Spotlight className="group mx-auto grid max-w-sm items-start gap-6 md:max-w-none md:grid-cols-3">
        <>
          {json?.cards?.map((card) => {
            const asset = assets?.find((asset) => asset?.sys?.id === card.icon);
            return (
              <SpotlightCardContainer
                key={card.title}
                className="relative z-20 h-full overflow-hidden bg-primary-neutral-50"
              >
                <SpotlightCard icon={asset} title={card.title} body={card.body} />
              </SpotlightCardContainer>
            );
          })}
        </>
      </Spotlight>
    </div>
  );
}
